import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Newspaper, MessageCircle, Radio } from "lucide-react";
import { http } from "../lib/api";

const LANES = [
  { id: "center", label: "Center", subtitle: "Wire-style coverage from the broad middle — Reuters · AP · NPR · BBC.", color: "#0e2d63" },
  { id: "conservative", label: "Conservative", subtitle: "Bill O'Reilly · Glenn Beck · Salem · War Room · The Dispatch · Daily Wire", color: "#7c2d12" },
  { id: "left", label: "Left", subtitle: "Center-left & left voices — The Atlantic · NYT · Mother Jones · The Intercept · NPR features", color: "#1e3a8a" },
];

export default function OtherVoices() {
  const [active, setActive] = useState("conservative");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (alive) setLoading(true);
      try {
        const r = await http.get(`/news/other-voices?lane=${active}&limit=40`);
        if (!alive) return;
        setItems(r.data || []);
      } catch {
        if (!alive) return;
        setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [active]);

  const lane = LANES.find((l) => l.id === active);

  return (
    <>
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">

        {/* Page intro */}
        <div className="mb-6 max-w-[860px]">
          <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)] mb-1">Other Voices</div>
          <h1 className="text-[28px] sm:text-[36px] font-black text-[var(--ink)] tracking-[-0.02em] leading-tight">Stories from outside the Newseze wire.</h1>
          <p className="text-[13.5px] text-[var(--muted)] mt-2 leading-snug">
            Newseze itself is calm and label-free — Other Voices is where you can read explicitly partisan or independent
            commentary on your own terms. Drama, conspiracy, and respect filters still apply, so even here you only see
            the calmer end of each lane.
          </p>
        </div>

        {/* Lane switcher */}
        <div className="flex flex-wrap gap-2 mb-5 border-b border-[var(--line)]" data-testid="ov-lane-switcher">
          {LANES.map((l) => (
            <button
              key={l.id}
              onClick={() => setActive(l.id)}
              className={`px-4 py-2 -mb-px border-b-2 text-[12px] uppercase tracking-[0.14em] font-black transition ${
                active === l.id
                  ? "border-[var(--gold)] text-[var(--ink)]"
                  : "border-transparent text-[var(--muted)] hover:text-[var(--ink)] hover:border-[var(--line)]"
              }`}
              data-testid={`ov-lane-${l.id}`}
              style={active === l.id ? { color: l.color } : {}}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="mb-4 text-[13px] text-[var(--muted)] italic" data-testid="ov-lane-subtitle">
          {lane?.subtitle}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="rounded-md border border-[var(--line)] bg-white animate-pulse p-3 h-[140px]" data-testid={`ov-skel-${i}`} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16" data-testid="ov-empty">
            <Radio size={28} className="mx-auto text-[var(--muted)] mb-3" />
            <div className="text-[15px] font-bold text-[var(--ink)] mb-1">No {lane?.label} voices in the wire right now.</div>
            <p className="text-[13px] text-[var(--muted)]">            Check back after the next hourly refresh.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ov-grid">
            {items.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.id}`}
                className="group bg-white rounded-md border border-[var(--line)] overflow-hidden flex flex-col hover:border-[var(--gold)] hover:shadow-md transition"
                data-testid={`ov-card-${a.id}`}
              >
                {a.image && (
                  <div className="aspect-[16/10] bg-cover bg-center" style={{ backgroundImage: `url(${a.image})` }} />
                )}
                <div className="p-3.5 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-black mb-1" style={{ color: lane?.color }}>
                    {lane?.label}
                  </div>
                  <div className="text-[14.5px] font-black leading-snug text-[var(--ink)] group-hover:text-[var(--blue)] line-clamp-3 mb-1.5">
                    {a.title}
                  </div>
                  {(a.summary || a.why_it_matters) && (
                    <p className="text-[12px] text-[var(--muted)] leading-snug line-clamp-2 mb-2">
                      {(a.summary || a.why_it_matters || "").slice(0, 160)}
                    </p>
                  )}
                  <div className="mt-auto flex items-center justify-between text-[10.5px] text-[var(--muted)]">
                    <span className="uppercase tracking-[0.12em] font-bold opacity-70">Wire: {a.source}</span>
                    {typeof a.trust === "number" && a.trust > 0 && (
                      <span className="text-[var(--blue)] font-black uppercase tracking-[0.12em]">trust {a.trust}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-10 rounded-md bg-[var(--sky)] border border-[var(--line)] p-4 text-[12.5px] text-[var(--muted)] max-w-[860px]" data-testid="ov-disclaimer">
          <div className="font-black text-[var(--ink)] mb-1">About Other Voices</div>
          Newseze does not endorse opinions in any lane. Stories here are aggregated from public RSS feeds and link out
          to the originating publisher. The Newseze trust score still reflects evidence, balance, impact, and respect —
          no matter which lane you&apos;re reading.
        </div>
      </main>
      <Footer />
    </>
  );
}
